<template>
  <Teleport to="body">
    <transition name="maint-slide">
      <div v-if="active" class="maint-banner" role="alert">
        <button v-if="hasSidebar" class="maint-menu" title="Meniu (deschide/închide)" @click="toggleSidebar">
          <i class="dx-icon dx-icon-menu" />
        </button>
        <i class="dx-icon dx-icon-warning maint-icon" />
        <div class="maint-body">
          <span class="maint-msg">{{ message }}</span>
          <span v-if="countdown" class="maint-count">Timp rămas: {{ countdown }}</span>
        </div>
        <button class="maint-close" title="Ascunde" @click="active = false">×</button>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as signalR from "@microsoft/signalr";
import AppSettings from "@/settings/appSettings";

export default defineComponent({
  name: "MaintenanceBanner",
  data() {
    return {
      connection: null as signalR.HubConnection | null,
      active: false,
      message: "",
      deadline: 0,
      countdown: "",
      timer: null as any,
      hasSidebar: false,
    };
  },
  mounted() {
    if (!AppSettings.API_Url_AuthSignalR) {
      // URL-ul hub-ului nu e configurat (ex. dev server pornit înainte de setarea
      // variabilei de mediu) — nu ne conectăm, dar nu blocăm aplicația.
      return;
    }
    this.connection = new signalR.HubConnectionBuilder()
      // withCredentials: true — CORS-ul Auth reflectă origin-ul și permite credentials
      // (aliniat cu modulul Comercial / ComercialHub).
      .withUrl(AppSettings.API_Url_AuthSignalR, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    this.connection.on("MaintenanceNotice", (payload: any) => {
      // SignalR serializează în camelCase (message/minutes), dar acceptăm și PascalCase.
      this.message = payload?.Message ?? payload?.message
        ?? "Platforma va fi oprită în curând pentru un update. Vă rugăm să salvați ce lucrați.";
      const minutes = Number(payload?.Minutes ?? payload?.minutes) || 0;
      this.deadline = minutes > 0 ? Date.now() + minutes * 60000 : 0;
      this.hasSidebar = !!document.querySelector(".menu-btn");
      this.active = true;
      this.startTimer();
    });

    this.connection.on("MaintenanceCleared", () => {
      this.active = false;
      this.stopTimer();
    });

    this.connection.start().catch(() => {
      /* fără notificări de mentenanță dacă hub-ul nu e disponibil — nu blocăm aplicația */
    });
  },
  beforeUnmount() {
    this.stopTimer();
    this.connection?.stop().catch(() => undefined);
  },
  methods: {
    // Banner-ul acoperă bara de sus, deci butonul nativ de meniu e dedesubt. Îl declanșăm
    // programatic (v-app-bar-nav-icon are clasa .menu-btn în HomeView) ca sidebar-ul să
    // rămână controlabil din banner.
    toggleSidebar() {
      const btn = document.querySelector(".menu-btn") as HTMLElement | null;
      btn?.click();
    },
    startTimer() {
      this.stopTimer();
      if (!this.deadline) {
        this.countdown = "";
        return;
      }
      this.tick();
      this.timer = setInterval(this.tick, 1000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    tick() {
      const ms = this.deadline - Date.now();
      if (ms <= 0) {
        this.countdown = "acum";
        this.stopTimer();
        return;
      }
      const total = Math.floor(ms / 1000);
      const m = Math.floor(total / 60);
      const s = total % 60;
      this.countdown = `${m}:${s.toString().padStart(2, "0")}`;
    },
  },
});
</script>

<style scoped>
.maint-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 14px 18px;
  /* fundal distinct de header-ul roșu al aplicației: roșu închis + dungi de avertizare */
  background: repeating-linear-gradient(
      45deg,
      #ffe000,
      #ffe000 18px,
      #ffc800 18px,
      #ffc800 36px
    );
  color: #3d2e00;
  border-bottom: 3px solid #3d2e00;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.45);
  font-family: Roboto, sans-serif;
  animation: maint-pulse 1.4s ease-in-out infinite;
}
@keyframes maint-pulse {
  0%,
  100% {
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.4);
  }
  50% {
    box-shadow: 0 4px 28px rgba(255, 200, 0, 0.95);
  }
}
.maint-menu {
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  padding: 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #3d2e00;
  cursor: pointer;
}
.maint-menu:hover {
  background: rgba(0, 0, 0, 0.14);
}
.maint-menu .dx-icon {
  font-size: 24px;
  color: #3d2e00;
}
.maint-icon {
  font-size: 26px;
  flex-shrink: 0;
  color: #3d2e00;
}
.maint-body {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 6px 18px;
  min-width: 0;
}
.maint-msg {
  font-size: 16px;
  font-weight: 700;
}
.maint-count {
  font-size: 14px;
  font-weight: 800;
  background: #3d2e00;
  color: #ffe000;
  padding: 3px 12px;
  border-radius: 12px;
  white-space: nowrap;
}
.maint-close {
  position: absolute;
  right: 14px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #3d2e00;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.85;
}
.maint-close:hover {
  opacity: 1;
}
.maint-slide-enter-active,
.maint-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.maint-slide-enter-from,
.maint-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
